import { store } from 'store/store';

import { isJwtExpiredOrInvalid } from 'utils/jwt';

import {
  getAdminAccessToken,
  getAdminIdToken,
  getUserAccessToken,
  getUserIdToken,
} from './authTokenStorage';
import { refreshSessionTokens } from './refreshSession';
import { getSessionRealm, setSessionRealm, type SessionRealm } from './sessionRealm';
import { hydrateStoreFromTabSession } from './sessionTokenTabStorage';
import { terminateSession } from './terminateSession';

export type SessionValidationResult = 'anonymous' | 'valid' | 'refreshed' | 'expired';

const getRealmBearerToken = (realm: SessionRealm): string | null =>
  realm === 'admin'
    ? (getAdminIdToken() ?? getAdminAccessToken())
    : (getUserIdToken() ?? getUserAccessToken());

const inferSessionRealm = (): SessionRealm | null => {
  const storedRealm = getSessionRealm();
  if (storedRealm) {
    return storedRealm;
  }

  const hasAdminToken = Boolean(getRealmBearerToken('admin'));
  const hasUserToken = Boolean(getRealmBearerToken('user'));

  if (hasAdminToken === hasUserToken) {
    if (!hasAdminToken || typeof window === 'undefined') {
      return null;
    }

    return window.location.pathname.startsWith('/admin') ? 'admin' : 'user';
  }

  return hasAdminToken ? 'admin' : 'user';
};

/**
 * Boot-time session check after a tab refresh.
 * Restores tab sessionStorage tokens into Redux before deciding auth state so a
 * normal refresh does not look logged-out and bounce to the login screen.
 */
export async function validatePersistedSession(): Promise<SessionValidationResult> {
  // Must run before reading tokens — Redux session slices are empty on cold load.
  hydrateStoreFromTabSession(store.dispatch);

  const realm = inferSessionRealm();
  if (!realm) {
    return 'anonymous';
  }

  setSessionRealm(realm);
  const bearerToken = getRealmBearerToken(realm);
  if (bearerToken && !isJwtExpiredOrInvalid(bearerToken)) {
    return 'valid';
  }

  const hadInMemorySession = Boolean(bearerToken);

  try {
    // skipExpireHandler: boot path terminates once below with the correct user/admin realm.
    const refreshed = await refreshSessionTokens({ skipExpireHandler: true });
    const refreshedBearer = refreshed?.idToken ?? refreshed?.accessToken ?? null;
    if (refreshedBearer && !isJwtExpiredOrInvalid(refreshedBearer)) {
      return 'refreshed';
    }
  } catch {
    // Session cleanup below is shared by invalid and rejected refresh tokens.
  }

  // Tokens live in Redux and a tab sessionStorage mirror. A leftover realm key
  // with no bearer/refresh token is unrestorable, not expired — do not purge
  // euId / journey helpers on every refresh.
  if (!hadInMemorySession) {
    return 'anonymous';
  }

  // Real expiry (had tokens, refresh failed) — send user/admin to their login.
  await terminateSession(realm);
  return 'expired';
}
